import { useState } from 'react';
import { Button, FormInput } from "../../../components";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken } from "../../../slices/staffSlice";
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff, dateToHumanFormat, getPaymentDates } from "../../../utils/constants";
import useConfigOptions from "../../../hooks/useConfigOptions";
import { useNavigate } from 'react-router-dom';
import { show } from '../../../slices/toastSlice';

import PreApproval from "./PreApproval";

function ManualDisburse({ loan }) {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loanTypesOld } = useConfigOptions();
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();


    const [confirmedClient, setConfirmedClient] = useState(false);
    const [transactionID, setTransactionID] = useState('');
    const [disbursementDate, setDisbursementDate] = useState(`${new Date().toISOString().split('T')[0]}`);
    const [repaymentDate, setRepaymentDate] = useState(`${loan?.paymentDates[0].split('T')[0]}`);
    const [amountToDisburse, setAmountToDisburse] = useState(loan?.amountReq);
    const [processing, setProcessing] = useState(false);
    const [paymentDates, setPaymentDates] = useState([]);

    async function calculateDates(date) {
        let paymentDates = await getPaymentDates(date, loan?.accountInfo?.numPayments);
        setPaymentDates(paymentDates);
    }

    const handleDisbursement = async(e) => {
        e.preventDefault();
        setProcessing(true);

        let url = `/loans/staff/disburse-loan`;
        let headers = authStaff(accessToken);

        let data = {
            _id : loan?._id,
            type : loan?.type.toLowerCase(),
            state : loan?.state,
            transactionType : 'disburse',
            transactionID : transactionID.replace(/[^a-zA-Z0-9]/g, ''),
            disbursementDate,
            repaymentDate,
            amount : amountToDisburse,
        }

        let res = await fetchRequests(url, "POST", headers, {}, data);
        setProcessing(false);
        
        if(res?.code === 401){
            // clear local storage and redirect to login
            sessionStorage.clear();
			navigate("/login");
            dispatch(show({
                type : "error",
                state : true,
                message : "Your session has expired. Please login to continue."
            }))
            return;
        }

        if(res?.err){
            dispatch(show({
                type : "error",
                state : true,
                message : res?.err
            }))
            return;
        }

        dispatch(show({
            type : "success",
            state : true,
            message : `Disbursement submitted successfully.`
        }))

        setTimeout(() => {
            navigate("/active-loans")
        },1500)
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mt-4">
            <div className="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Manual Disbursement</h4>
            </div>
            <div className="px-5 py-5">
                <form onSubmit={handleDisbursement} className="space-y-5">
                    <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5'>
                        <div>
                            <label htmlFor="transactionID" className='form-label'>Transaction ID</label>
                            <FormInput
                                type="text"
                                name="transactionID"
                                id="transactionID"
                                required
                                disabled={false}
                                value={transactionID}
                                setValue={setTransactionID}
                            />
                        </div>

                        <div>
                            <label htmlFor="disbursementDate" className='form-label'>Disbursement Date</label>
                            <input
                                type="date"
                                name={"disbursementDate"}
                                id={"disbursementDate"}
                                className="form-input disabled:opacity-75 disabled:bg-gray-200 placeholder:text-gray-700 disabled:cursor-not-allowed"
                                required
                                value={disbursementDate}
                                onChange={e => setDisbursementDate(e.target.value)}
                                min={new Date(year, month - 1, 1).toISOString().split('T')[0]}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div>
                            <label htmlFor="repaymentDate" className='form-label'>Repayment Start Date</label>
                            <input
                                type="date"
                                name={"repaymentDate"}
                                id={"repaymentDate"}
                                className="form-input disabled:opacity-75 disabled:bg-gray-200 placeholder:text-gray-700 disabled:cursor-not-allowed"
                                required={false}
                                value={repaymentDate}
                                onChange={e => {
                                    setRepaymentDate(e.target.value);
                                    calculateDates(e.target.value)
                                }}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div>
                            <label htmlFor="amountToDisburse" className='form-label'>Amount to Disburse</label>
                            <FormInput
                                type="number"
                                name="amountToDisburse"
                                id="amountToDisburse"
                                required
                                value={amountToDisburse}
                                setValue={setAmountToDisburse}
                            />
                        </div>

                        <div>
                            <label htmlFor="customerName" className='form-label'>Name of Customer</label>
                            <FormInput
                                type="text"
                                name="customerName"
                                id="customerName"
                                required
                                disabled={true}
                                value={`${loan?.client?.otherNames} ${loan?.client?.surname}`}
                            />
                        </div>

                        <div>
                            <label htmlFor="mobileNumber" className='form-label'>Mobile Number</label>
                            <FormInput
                                type="number"
                                name="mobileNumber"
                                id="mobileNumber"
                                required
                                disabled={true}
                                value={loan?.phone}
                            />
                        </div>
                    </div>

                    <div className='flex gap-3 p-4 rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/5 text-sm text-amber-800 dark:text-amber-300 leading-relaxed'>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 shrink-0 mt-0.5 text-amber-500">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <p>
                            If the loan is disbursed today without "Repayment Start Date" being altered, the repayment start date would be <strong>{dateToHumanFormat(loan?.startDate)}</strong> and would end <strong>{dateToHumanFormat(loan?.endDate)}</strong>. If the client has a "Standing Order" and this was indicated in the loan application, input the date the standing order would be activated as the "Repayment Start Date". The additional five (5) days would be added to the repayment end date.
                        </p>
                    </div>

                    {paymentDates?.length > 0 && (
                        <div className='flex gap-3 p-4 rounded-lg border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/5 text-sm text-emerald-800 dark:text-emerald-300 leading-relaxed'>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 shrink-0 mt-0.5 text-emerald-500">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <p>
                                The new repayment start date is set to <strong>{dateToHumanFormat(paymentDates[0])}</strong> and will end on <strong>{dateToHumanFormat(paymentDates[paymentDates.length - 1])}</strong>.
                            </p>
                        </div>
                    )}

                    {(loan?.type === loanTypesOld.cediscredit && !confirmedClient) && (
                        <div>
                            <PreApproval
                                loan={loan}
                                setConfirmedClient={setConfirmedClient}
                            />
                        </div>
                    )}

                    <div className="pt-1">
                        <Button
                            btnText={"Disburse"}
                            btnType="submit"
                            btnClasses={"form-button w-auto"}
                            disabled={
                                loan?.type === loanTypesOld.cediscredit ? (loan?.type === loanTypesOld.cediscredit && !confirmedClient) || processing : processing
                            }
                            processing={processing}
                        />
                    </div>
                </form>
            </div>
        </div>
    )
}

export default ManualDisburse